import { UDPGameClient } from '../utils/UDPGameClient';

// Use the ChatMessage interface from UDPGameClient
interface ChatMessage {
    sender_id: string;
    sender_name: string;
    content: string;
    timestamp: number;
}

export class ChatUI {
    private container!: HTMLDivElement;
    private messagesContainer!: HTMLDivElement;
    private input!: HTMLInputElement;
    private messages: ChatMessage[] = [];
    private maxMessages: number = 50;
    private isVisible: boolean = true;
    private inputController: any = null; // Will hold the InputController instance

    constructor(private client: UDPGameClient) {
        this.createUI();
        this.setupEventListeners();
        
        // Find InputController instance - we'll try to get it globally
        // This will be set later once we find it
        this.tryToGetInputController();
        
        // Load existing chat history if available
        const chatHistory = this.client.getChatHistory();
        if (chatHistory && chatHistory.length > 0) {
            chatHistory.forEach(msg => this.addMessage(msg));
        }
    }

    private tryToGetInputController(): void {
        // Try to find the InputController instance
        // We'll check frequently until we find it (in case it's not ready yet)
        const checkInterval = setInterval(() => {
            // Look for any global reference to the InputController
            const globalAny = window as any;
            if (globalAny.inputController) {
                this.inputController = globalAny.inputController;
                clearInterval(checkInterval);
                console.log('ChatUI found InputController');
            }
        }, 500);
        
        // Stop checking after 10 seconds to avoid infinite loop
        setTimeout(() => clearInterval(checkInterval), 10000);
    }

    private createUI() {
        // Create main container
        this.container = document.createElement('div');
        this.container.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 20px;
            width: 300px;
            height: 200px;
            background-color: rgba(0, 0, 0, 0.7);
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            padding: 10px;
            font-family: Arial, sans-serif;
            z-index: 1000;
        `;

        // Create header with title and toggle button
        const header = document.createElement('div');
        header.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 5px;
            color: white;
            font-weight: bold;
        `;
        
        const title = document.createElement('span');
        title.textContent = 'Chat (Press Enter to type)';
        
        const toggleButton = document.createElement('button');
        toggleButton.textContent = '−';
        toggleButton.style.cssText = `
            background: none;
            border: none;
            color: white;
            font-size: 16px;
            cursor: pointer;
            padding: 0 5px;
        `;
        toggleButton.addEventListener('click', () => this.toggleMinimize());
        
        header.appendChild(title);
        header.appendChild(toggleButton);

        // Create messages container
        this.messagesContainer = document.createElement('div');
        this.messagesContainer.style.cssText = `
            flex: 1;
            overflow-y: auto;
            margin-bottom: 10px;
            color: white;
            font-size: 14px;
            padding-right: 5px;
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.5) transparent;
        `;

        // Create input container
        const inputContainer = document.createElement('div');
        inputContainer.style.cssText = `
            display: flex;
            gap: 5px;
        `;

        // Create input field
        this.input = document.createElement('input');
        this.input.type = 'text';
        this.input.placeholder = 'Press Enter to type, Enter to send...';
        this.input.style.cssText = `
            flex: 1;
            padding: 8px;
            border: none;
            border-radius: 4px;
            background-color: rgba(255, 255, 255, 0.9);
            color: black;
        `;

        // Add special data attribute for the InputController to detect
        this.input.dataset.chatInput = 'true';
        
        // Add focus and blur handlers to explicitly set typing state
        this.input.addEventListener('focus', () => {
            console.log('Chat input focused, disabling movement keys');
            if (this.inputController && typeof this.inputController.setTypingState === 'function') {
                this.inputController.setTypingState(true);
            }
        });
        
        this.input.addEventListener('blur', () => {
            console.log('Chat input blurred, enabling movement keys');
            if (this.inputController && typeof this.inputController.setTypingState === 'function') {
                this.inputController.setTypingState(false);
            }
        });

        // Assemble UI
        inputContainer.appendChild(this.input);
        this.container.appendChild(header);
        this.container.appendChild(this.messagesContainer);
        this.container.appendChild(inputContainer);
        document.body.appendChild(this.container);
        
        // Initially blur the input to allow global keyboard shortcuts
        this.input.blur();
    }

    private setupEventListeners() {
        // Handle chat messages from server
        this.client.on('chat_message', (message: ChatMessage) => {
            console.log('Chat message received in UI:', message);
            this.addMessage(message);
        });

        // Handle enter key press in input field
        this.input.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                console.log('Enter key pressed in input, sending message');
                this.sendMessage();
                // Blur the input after sending to allow global keyboard shortcuts again
                this.input.blur();
                
                // Explicitly set typing state to false
                if (this.inputController && typeof this.inputController.setTypingState === 'function') {
                    this.inputController.setTypingState(false);
                }
                
                e.preventDefault();
            }
        });
        
        // Handle Escape key to cancel typing
        this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                console.log('Escape pressed, canceling message');
                this.input.value = '';
                this.input.blur();
                
                // Explicitly set typing state to false
                if (this.inputController && typeof this.inputController.setTypingState === 'function') {
                    this.inputController.setTypingState(false);
                }
                
                e.preventDefault();
            }
        });
        
        // Focus input when clicking on the chat container
        this.container.addEventListener('click', (e) => {
            // Don't focus if clicking on a button
            if (!(e.target instanceof HTMLButtonElement)) {
                this.input.focus();
            }
        });
        
        // Add global keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            // Toggle chat visibility with T key
            if (e.key === 't' && !this.isInputFocused()) {
                this.toggle();
                e.preventDefault();
            }
            
            // Open chat with Enter key when not already focused
            if (e.key === 'Enter' && !this.isInputFocused()) {
                if (this.isVisible) {
                    this.input.focus();
                    
                    // Explicitly set typing state to true
                    if (this.inputController && typeof this.inputController.setTypingState === 'function') {
                        this.inputController.setTypingState(true);
                    }
                    
                    e.preventDefault();
                } else {
                    this.show();
                    this.input.focus();
                    
                    // Explicitly set typing state to true
                    if (this.inputController && typeof this.inputController.setTypingState === 'function') {
                        this.inputController.setTypingState(true);
                    }
                    
                    e.preventDefault();
                }
            }
        });
    }
    
    private isInputFocused(): boolean {
        return document.activeElement === this.input;
    }

    private sendMessage() {
        const message = this.input.value.trim();
        if (message) {
            console.log('Sending chat message:', message);
            try {
                // Use the correct method name from UDPGameClient
                this.client.sendChatMessage(message);
                console.log('Chat message sent successfully');
                
                // Clear input field
                this.input.value = '';
                
                // Remove local echo since we'll receive the message back from the server
                // with the correct player name
            } catch (error) {
                console.error('Error sending chat message:', error);
            }
        } else {
            console.log('Attempted to send empty message, ignoring');
        }
    }

    private addMessage(message: ChatMessage) {
        // Add message to array
        this.messages.push(message);
        if (this.messages.length > this.maxMessages) {
            this.messages.shift();
        }

        // Create message element
        const messageElement = document.createElement('div');
        messageElement.style.cssText = `
            margin-bottom: 5px;
            word-wrap: break-word;
        `;

        const time = new Date(message.timestamp).toLocaleTimeString();
        messageElement.innerHTML = `
            <span style="color: #4CAF50;">[${time}] ${message.sender_name}:</span>
            <span style="margin-left: 5px;">${this.escapeHtml(message.content)}</span>
        `;

        // Add message to container
        this.messagesContainer.appendChild(messageElement);
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }

    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    private toggleMinimize() {
        const contentElements = [
            this.messagesContainer, 
            this.container.querySelector('div:last-child') as HTMLDivElement
        ];
        const toggleButton = this.container.querySelector('button') as HTMLButtonElement;
        
        if (contentElements[0]?.style.display === 'none') {
            // Expand
            contentElements.forEach(el => {
                if (el) (el as HTMLElement).style.display = '';
            });
            this.container.style.height = '200px';
            if (toggleButton) toggleButton.textContent = '−';
        } else {
            // Collapse
            contentElements.forEach(el => {
                if (el) (el as HTMLElement).style.display = 'none';
            });
            this.container.style.height = 'auto';
            if (toggleButton) toggleButton.textContent = '+';
        }
    }

    public show() {
        this.container.style.display = 'flex';
        this.isVisible = true;
    }

    public hide() {
        this.container.style.display = 'none';
        this.isVisible = false;
    }

    public toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }
} 